import { useEffect, useMemo, useRef } from 'react';
import { useUnifiedSizing } from '@/sizing/useUnifiedSizing';
import { useSizingStateV2 } from './useSizingStateV2';
import type { ScenarioPreset, UserType } from '@/types';
import type { PersonaProfile } from '@/types/personas';

type UseSizingStateArgs = {
  personaProfiles?: PersonaProfile[];
};

export function useSizingEngineV2(args: UseSizingStateArgs) {
  const state = useSizingStateV2();
  const unified = useUnifiedSizing();
  const initializedRef = useRef(false);

  useEffect(() => {
    const existingUsers = state.scenario.users as any[];

    const profilesFromArgs = Array.isArray(args.personaProfiles)
      ? (args.personaProfiles as PersonaProfile[])
      : [];

    if (process.env.NODE_ENV !== 'production') {
      console.log('[useSizingEngineV2] init check', {
        initialized: initializedRef.current,
        usersCount: existingUsers.length,
        profilesFromArgs: profilesFromArgs.length,
      });
    }

    if (initializedRef.current) return;

    // Try to load profiles from args, then from localStorage
    const profiles =
      profilesFromArgs.length > 0
        ? profilesFromArgs
        : (() => {
            try {
              const cached = window?.localStorage?.getItem('personaProfilesForSizing');
              if (!cached) return null;
              const parsed = JSON.parse(cached);
              return Array.isArray(parsed) ? (parsed as PersonaProfile[]) : null;
            } catch {
              return null;
            }
          })();

    if (!profiles || profiles.length === 0) return;

    // If we already have users but none are bound to a persona, treat them as placeholders
    const hasRealPersonaBindings = existingUsers.some(
      (u) => u.profileId || u.profile_id || u.personaId,
    );

    if (existingUsers.length > 0 && !hasRealPersonaBindings) {
      state.resetScenario(); // clears users + tradeoffs
    }

    // After reset, re-read users
    const usersAfterReset = (state.scenario.users as any[]) ?? [];
    if (usersAfterReset.length > 0 && initializedRef.current) return;

    // Seed explicit personas based on latest defaults: Data Analyst, Email Drafter, Quick Lookup
    const desired = [
      { id: 'data-analyst', pct: 50 },
      { id: 'email-drafter', pct: 30 },
      { id: 'quick-lookup', pct: 20 },
    ];

    const seeded = desired.map(({ id, pct }) => {
      const profile = profiles.find((p) => p.id === id);

      const inTok =
        profile?.input_mean ??
        profile?.prompt_tokens_per_turn ??
        400;

      const outTok =
        profile?.output_mean ??
        profile?.response_tokens_per_turn ??
        300;

      const read_sec =
        profile?.read_sec_default ??
        3;

      const digest_sec =
        profile?.think_sec_default ??
        5;

      return {
        uid: profile?.id ?? id,
        id: profile?.id ?? id,
        label: profile?.display_name ?? profile?.name ?? id,
        pct,
        profileId: profile?.id ?? id, // critical for UI to treat as real persona
        inTok,
        outTok,
        read_sec,
        digest_sec,
      } as Partial<UserType> & { profileId: string };
    });

    // Normalize to 100% just in case
    const totalPct = seeded.reduce((sum, p) => sum + p.pct, 0) || 1;
    let remaining = 100;
    const normalized = seeded.map((p, idx) => {
      const pct = idx === seeded.length - 1 ? remaining : Math.round((p.pct / totalPct) * 100);
      remaining -= pct;
      return { ...p, pct };
    });

    state.setTradeoff('concurrentUsers', 2000);
    state.setTradeoff('turns', 3);

    normalized.forEach((u) => state.addUser(u));

    initializedRef.current = true;
  }, [args.personaProfiles, state]);

  const merged = useMemo(
    () => ({
      scenario: state.scenario,
      setTradeoff: state.setTradeoff,
      setAdvanced: state.setAdvanced,
      updateUser: state.updateUser,
      removeUser: state.removeUser,
      addUser: state.addUser,
      setSelectedModelId: state.setSelectedModelId,
      applyPreset: state.applyPreset,
      resetScenario: state.resetScenario,
      shareScenario: state.shareScenario,
      personas: state.scenario.users,
      status: unified.status,
      snapshot: unified.snapshot,
      error: unified.error ?? undefined,
      compute: unified.runSizing,
      loading: unified.status === 'running',
    }),
    [state, unified],
  );

  if (process.env.NODE_ENV !== 'production') {
    console.log('[useSizingEngineV2]', {
      concurrentUsers: merged.scenario.tradeoffs.concurrentUsers,
      status: merged.status,
      hasSnapshot: Boolean(merged.snapshot),
    });
  }

  return merged;
}

const PRESETS: ScenarioPreset[] = [
  // unchanged presets
];

export { PRESETS as SCENARIO_PRESETS };
export type UseSizingEngineV2Return = ReturnType<typeof useSizingEngineV2>;
